// ============================================================================
// Feuille du noyau : DÉRIVÉES PARTIELLES (refonte, lot 6.69, moteur v64).
// ============================================================================
// L'énoncé : une fonction de deux variables, « f(x, y) = x² × y » (aussi en polaires,
// « F(r, θ) = r² »). La question demande une dérivée partielle, une dérivée seconde, le
// gradient, le laplacien, une de ces dérivées en un point, une expression de dérivées
// (« x × ∂f/∂x + y × ∂f/∂y ») ou, le long d'une courbe x = x(t), y = y(t), la dérivée de
// g(t) = f(x(t), y(t)) (règle de la chaîne). L'élève écrit, une étape par ligne :
//
//   « ∂f/∂x = 2x × y »               dériver par rapport à x (y constant)       2
//   « ∂²f/∂x∂y = 6x × y² »           dériver une dérivée partielle               2
//   « ∇f = (2x, 2y) »                le gradient (les deux dérivées écrites)     1
//   « Δf = … »                       le laplacien (les deux dérivées secondes)   1
//   « ∂f/∂x(1, 2) = 2 »              la valeur en un point                       1
//   « F(r, θ) = r² »                 simplifier la fonction                      1
//   « x′(t) = −sin(t) »              dériver une coordonnée de la courbe         1
//   « g(t) = t² × e^t »              composer                                    1
//   « g′(t) = x′(t) × ∂f/∂x + … »    la règle de la chaîne                       2
//   « g′(t) = 2t × e^t + t² × e^t »  dériver g(t) (une variable)                 2
//   remplacer (les dérivées par leurs expressions, x et y par x(t), y(t))        1
//   calculer (une écriture plus courte)                                          1
//
// « ∂f/∂x », « ∂²f/∂x∂y », « ∇f », « Δf », « ∂f/∂x(1, 2) », « x′(t) », « g′(t) », « g′(0) »
// sont des bouts. Vérité ÉCHANTILLONNÉE (comme les dérivées du lot 5.9) : les deux membres
// d'une ligne, dérivées remplacées par leur valeur exacte (dérivation symbolique, l'autre
// variable constante), ont les mêmes valeurs en 9 points du plan (ou 9 instants de la
// courbe). Une ligne fausse ne dit jamais la bonne réponse ; une ligne juste qui saute des
// étapes reste sur la feuille sans HORA (D7).
// ============================================================================

import type { Chip } from './lexer';
import { isNum, lex, normalize } from './lexer';
import type { ScratchModel } from './model';
import { atomsText, parseLine, type Line, type Node } from './tree';
import { add, canon, fromNode, substitute, symbolsOf, termKey, type Term } from './kernel/term';
import { evalFloat } from './kernel/normal';
import { derive } from './kernel/calculus';

export const PARTIAL_STEP_IDS = {
  first: 'partial.first@1',
  second: 'partial.second@1',
  gradient: 'partial.gradient@1',
  laplacian: 'partial.laplacian@1',
  point: 'partial.point@1',
  simplify: 'partial.simplify@1',
  substitute: 'partial.substitute@1',
  curve: 'partial.curve@1',
  compose: 'partial.compose@1',
  chain: 'partial.chain@1',
  derive: 'partial.derive@1',
  compute: 'kernel.compute@1',
} as const;
type StepKind = keyof typeof PARTIAL_STEP_IDS;

const STEP: Record<StepKind, { label: string; hora: number; why: string }> = {
  first: {
    label: 'Dériver par rapport à x',
    hora: 2,
    why: 'Une dérivée partielle : on dérive par rapport à une variable, l’autre est une constante.',
  },
  second: {
    label: 'Dériver une dérivée partielle',
    hora: 2,
    why: 'Une dérivée seconde : on dérive une dérivée partielle déjà calculée.',
  },
  gradient: {
    label: 'Gradient',
    hora: 1,
    why: 'Le gradient est le couple des dérivées partielles.',
  },
  laplacian: {
    label: 'Laplacien',
    hora: 1,
    why: 'Le laplacien est la somme des deux dérivées secondes.',
  },
  point: {
    label: 'Calculer en un point',
    hora: 1,
    why: 'On remplace les variables par les coordonnées du point.',
  },
  simplify: {
    label: 'Simplifier la fonction',
    hora: 1,
    why: 'Une écriture plus simple de la fonction.',
  },
  substitute: {
    label: 'Remplacer',
    hora: 1,
    why: 'On remplace par ce qui est déjà calculé.',
  },
  curve: {
    label: 'Dériver x(t)',
    hora: 1,
    why: 'La dérivée d’une coordonnée de la courbe.',
  },
  compose: {
    label: 'Composer',
    hora: 1,
    why: 'On remplace x et y par x(t) et y(t) dans la fonction.',
  },
  chain: {
    label: 'Règle de la chaîne',
    hora: 2,
    why: 'g′(t) = x′(t) × ∂f/∂x + y′(t) × ∂f/∂y, les dérivées partielles prises en (x(t), y(t)).',
  },
  derive: {
    label: 'Dériver',
    hora: 2,
    why: 'Une fonction d’une variable : on la dérive.',
  },
  compute: { label: 'Calculer', hora: 1, why: 'Un calcul.' },
};

const CARD = 'Dérivées partielles';

export interface KPartVerdict {
  kind:
    | 'empty'
    | 'open'
    | 'building'
    | 'ready'
    | 'pending'
    | 'syntax'
    | 'trap'
    | 'false'
    | 'unjustified'
    | 'useless'
    | 'step';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  final?: boolean;
  hora?: number;
  /** D7 : ligne juste qui saute des étapes, gardée sans HORA */
  jump?: boolean;
  trap?: { line: Line; title: string; why: string };
  rule?: { id: string; card: string; label: string; hora: number; why: string };
  omove?: PMove;
}

/** ce que la ligne apprend au moteur */
interface PMove {
  text: string;
  key: string;
  size: number;
  heads: string[];
  vars: boolean;
  final?: boolean;
}

/** un bout « dérivée » : sa nature, ses variables, son point */
interface Head {
  /** clé : « d1:x », « d2:x,y » (dérivée croisée : variables triées), « grad »… (+ « @point ») */
  key: string;
  text: string;
  kind: 'f' | 'd1' | 'd2' | 'grad' | 'lap' | 'cd' | 'g' | 'g1' | 'g2';
  /** d1 : [v] ; d2 : [a, b] (∂/∂a de ∂f/∂b) ; cd : [v] */
  vars: string[];
  /** le point (« 1, 2 » ; « 0 » pour g′(0)) */
  pt?: string[];
}

interface Curve {
  t: string;
  g: string;
  X: Term;
  Y: Term;
}

interface Fact {
  size: number;
  heads: string[];
  vars: boolean;
}

export interface KPartState {
  model: ScratchModel;
  spec: KPartSpec;
  fn: string;
  vars: [string, string];
  F: Term;
  Fsize: number;
  curve: Curve | null;
  grid: Record<string, number>[];
  tEnvs: Record<string, number>[];
  target: { key: string; text: string };
  answer: { size: number; heads: number; text: string };
  facts: Map<string, Fact>;
  written: Set<string>;
  givenTexts: Set<string>;
  path: string[];
  traps: { text: string; line: Line; title: string; why: string }[];
  heads: Map<string, Head>;
  syms: Map<string, string>;
  termCache: Map<string, Term | [Term, Term] | null>;
  lines: number;
  concluded: boolean;
}

export class PartialModelError extends Error {}

/* ------------------------------ la question ------------------------------ */

/** la fonction, ses deux variables, la courbe (règle de la chaîne) */
export interface KPartSpec {
  fn: string;
  vars: [string, string];
  curve: { t: string; g: string; x: string; y: string } | null;
}

const esc = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function partialSpecOf(model: ScratchModel): KPartSpec {
  const k = model.kernel!;
  const m = /^\s*([A-Za-z])\s*\(\s*([\p{L}])\s*[,;]\s*([\p{L}])\s*\)\s*=/u.exec(k.start);
  if (!m)
    throw new PartialModelError(
      `Le départ définit la fonction : « f(x, y) = … » (« ${k.start} »).`,
    );
  const fn = m[1]!;
  const vars: [string, string] = [m[2]!, m[3]!];
  if (vars[0] === vars[1] || vars.includes(fn))
    throw new PartialModelError('Deux variables distinctes, autres que la fonction.');
  const c = k.partial?.curve;
  return { fn, vars, curve: c ? { t: c.t, g: c.g, x: c.x, y: c.y } : null };
}

/* ------------------------------ les bouts ------------------------------ */

const P = 'ΞΞΞ';
const CODES = 'ΑΒΓΕΖΗΙΚΛΜΝΟΡΤΥΦΧΨϘϚϜϠ';
const SEP = `${P}Ω`;
const SUP_DIGITS = '⁰¹²³⁴⁵⁶⁷⁸⁹';
const isSupRun = (a: string): boolean =>
  a.length > 0 && [...a].every((c) => SUP_DIGITS.includes(c));

/** un bout « dérivée » ou « fonction en un point » (protégé dans l'écriture) */
export function isPartialHeadAtom(a: string): boolean {
  return /^[∂∇Δ]/.test(a) || /^[\p{L}][′″]*\(.+\)$/u.test(a);
}

/** les arguments d'un point : « 1 ; 2 » → « 1, 2 » */
function argsOf(raw: string): string[] {
  return raw
    .split(/\s*[,;]\s*/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Atomes d'une ligne : « ∂f/∂x », « ∂²f/∂x²», « ∂²f/∂x∂y », « ∇f », « Δf », « f(x, y) »,
 * « ∂f/∂x(1, 2) », « f(1, 2) » ; le long d'une courbe « x′(t) », « g(t) », « g′(t) »,
 * « g″(t) », « g′(0) » ; « , » sépare les coordonnées d'un couple.
 */
export function kpartLineAtoms(text: string, spec: KPartSpec): string[] {
  const heads: string[] = [];
  const put = (h: string): string => {
    let i = heads.indexOf(h);
    if (i < 0) i = heads.push(h) - 1;
    return ` ${P}${CODES[i] ?? 'Ϟ'} `;
  };
  const F = esc(spec.fn);
  const V = `(${spec.vars.map(esc).join('|')})`;
  const PT = '(?:\\s*\\(([^()]*)\\))?';
  const fn = spec.fn;
  const vars = spec.vars;
  const point = (raw: string | undefined): string => {
    if (raw === undefined) return '';
    const a = argsOf(raw);
    return a.length === 2 && a[0] === vars[0] && a[1] === vars[1] ? '' : `(${a.join(', ')})`;
  };
  let t = text
    .replace(/[’']{2}/g, '″')
    .replace(/[’']/g, '′')
    .replace(/′′/g, '″');
  t = t
    .replace(
      new RegExp(`∂\\s*²\\s*${F}\\s*/\\s*∂\\s*${V}\\s*∂\\s*${V}${PT}`, 'gu'),
      (_m, a: string, b: string, p?: string) => put(`∂²${fn}/∂${a}∂${b}${point(p)}`),
    )
    .replace(
      new RegExp(`∂\\s*²\\s*${F}\\s*/\\s*∂\\s*${V}\\s*²${PT}`, 'gu'),
      (_m, a: string, p?: string) => put(`∂²${fn}/∂${a}²${point(p)}`),
    )
    .replace(new RegExp(`∂\\s*${F}\\s*/\\s*∂\\s*${V}${PT}`, 'gu'), (_m, a: string, p?: string) =>
      put(`∂${fn}/∂${a}${point(p)}`),
    )
    .replace(new RegExp(`∇\\s*${F}${PT}`, 'gu'), (_m, p?: string) => put(`∇${fn}${point(p)}`))
    .replace(new RegExp(`Δ\\s*${F}${PT}`, 'gu'), (_m, p?: string) => put(`Δ${fn}${point(p)}`));
  const c = spec.curve;
  if (c) {
    const T = esc(c.t);
    t = t
      .replace(new RegExp(`(?<![\\p{L}])${V}\\s*′\\s*\\(\\s*${T}\\s*\\)`, 'gu'), (_m, v: string) =>
        put(`${v}′(${c.t})`),
      )
      .replace(
        new RegExp(`(?<![\\p{L}])${esc(c.g)}\\s*(″|′)?\\s*\\(([^()]*)\\)`, 'gu'),
        (_m, p: string | undefined, a: string) => put(`${c.g}${p ?? ''}(${a.trim()})`),
      )
      .replace(
        new RegExp(`(?<![\\p{L}])${V}\\s*\\(\\s*${T}\\s*\\)`, 'gu'),
        (_m, v: string) => ` ${v} `,
      );
  }
  t = t.replace(new RegExp(`(?<![\\p{L}])${F}\\s*\\(([^()]*)\\)`, 'gu'), (_m, a: string) => {
    const p = point(a);
    return put(p ? `${fn}${p}` : `${fn}(${vars.join(', ')})`);
  });
  t = t.replace(/\s*;\s*|,\s+/g, ` ${SEP} `);
  const out: string[] = [];
  for (const a of lex(t)) {
    const last = out[out.length - 1];
    if (isSupRun(a) && last !== undefined && isSupRun(last)) {
      out[out.length - 1] = last + a;
      continue;
    }
    out.push(a);
  }
  const mapped = out.map((a) => {
    if (a === SEP) return ',';
    if (a.startsWith(P) && a.length === P.length + 1) {
      const i = CODES.indexOf(a[P.length]!);
      if (i >= 0 && heads[i] !== undefined) return heads[i]!;
    }
    return a;
  });
  return normalize(mapped);
}

/** texte d'une ligne (relu à l'identique par `kpartLineAtoms`) */
export function kpartText(atoms: string[]): string {
  const hs: string[] = [];
  const A = atoms.map((a) => {
    if (isPartialHeadAtom(a)) {
      hs.push(a);
      return `${P}${CODES[hs.length - 1] ?? 'Ϟ'}`;
    }
    return a === ',' ? SEP : a;
  });
  let s = atomsText(A)
    .replace(/ ([⁰¹²³⁴⁵⁶⁷⁸⁹]+)/g, '$1')
    .replace(/\//g, '÷')
    .replace(/\s*÷\s*/g, ' ÷ ')
    .replace(new RegExp(`\\s*${SEP}\\s*`, 'g'), ', ');
  hs.forEach((h, i) => {
    s = s.split(`${P}${CODES[i] ?? 'Ϟ'}`).join(h);
  });
  return s;
}

const lineOf = (atoms: string[]): Line => ({
  dis: [{ sides: [{ k: 's', n: kpartText(atoms) }] }],
});
/** Ligne de feuille (texte) pour l'app. */
export function kpartLine(text: string, spec: KPartSpec): Line {
  return lineOf(kpartLineAtoms(text, spec));
}

/** « x³ » → x ^ 3 pour le parseur (« ² » seul reste un carré) */
function caretAtoms(atoms: string[]): string[] {
  const out: string[] = [];
  for (const a of atoms) {
    if (isSupRun(a) && a !== '²') {
      out.push('^', [...a].map((c) => String(SUP_DIGITS.indexOf(c))).join(''));
      continue;
    }
    out.push(a);
  }
  return out;
}

/* ------------------------------ termes et valeurs ------------------------------ */

const E: Term = { k: 'fn', f: 'exp', a: { k: 'num', v: { n: 1n, d: 1n } } } as Term;
/** la constante e s'écrit « e » ; « e^(…) » est déjà lu exp(…) */
function withE(t: Term): Term {
  switch (t.k) {
    case 'sym':
      return t.n === 'e' ? E : t;
    case 'add':
    case 'mul':
      return { ...t, c: t.c.map(withE) } as Term;
    case 'div':
    case 'pow':
    case 'binom':
      return { ...t, a: withE(t.a), b: withE(t.b) } as Term;
    case 'fn':
      return { ...t, a: withE(t.a) } as Term;
    default:
      return t;
  }
}

function val(t: Term, env: Record<string, number>): number {
  try {
    const v = evalFloat(t, env);
    return Number.isFinite(v) ? v : NaN;
  } catch {
    return NaN;
  }
}

function size(t: Term): number {
  switch (t.k) {
    case 'num':
      return t.v.d === 1n ? 1 : 2;
    case 'sym':
      return 1;
    case 'add':
    case 'mul':
      return 1 + t.c.reduce((s, c) => s + size(c), 0);
    case 'div':
    case 'pow':
    case 'binom':
      return 1 + size(t.a) + size(t.b);
    case 'fn':
      return 1 + size(t.a);
  }
}

const near = (a: number, b: number): boolean =>
  Number.isFinite(a) &&
  Number.isFinite(b) &&
  Math.abs(a - b) <= 1e-7 * Math.max(1, Math.abs(a), Math.abs(b));

/** une expression (texte) en un terme : « 1 », « −2 », « π ÷ 2 » */
function exprTerm(text: string): Term {
  const A = normalize(caretAtoms(lex(text)));
  const line = parseLine(A, { chains: true });
  const s = line.dis[0]?.sides;
  if (!s || s.length !== 1 || line.dis.length !== 1) throw new PartialModelError(`« ${text} »`);
  return withE(fromNode(s[0]!));
}

const GRID: [number, number][] = [
  [0.7, 1.3],
  [1.9, -0.6],
  [-1.1, 0.45],
  [2.3, 1.7],
  [-0.8, -1.4],
  [0.35, 2.2],
  [1.25, 0.9],
  [-1.7, 0.6],
  [0.55, -0.85],
];
const T_PTS = [-1.3, -0.7, -0.2, 0.3, 0.8, 1.2, 1.7, 2.1, 2.6];

/* ------------------------------ les bouts « dérivée » ------------------------------ */

function headOf(ks: KPartState, a: string): Head | null {
  if (ks.heads.has(a)) return ks.heads.get(a)!;
  const h = parseHead(ks.spec, a);
  if (h) ks.heads.set(a, h);
  return h;
}

function parseHead(spec: KPartSpec, a: string): Head | null {
  const fn = esc(spec.fn);
  const V = `(${spec.vars.map(esc).join('|')})`;
  const PT = '(?:\\(([^()]*)\\))?';
  const mk = (kind: Head['kind'], vars: string[], pt?: string): Head => {
    const p = pt === undefined ? undefined : argsOf(pt);
    const base =
      kind === 'd1' || kind === 'cd'
        ? `${kind}:${vars[0]}`
        : kind === 'd2'
          ? `d2:${[...vars].sort((u, w) => spec.vars.indexOf(u) - spec.vars.indexOf(w)).join(',')}`
          : kind;
    return {
      key: p ? `${base}@${p.join(',')}` : base,
      text: a,
      kind,
      vars,
      ...(p ? { pt: p } : {}),
    };
  };
  let m: RegExpExecArray | null;
  if (a === spec.fn || a === `${spec.fn}(${spec.vars.join(', ')})`) return mk('f', []);
  if ((m = new RegExp(`^${fn}\\(([^()]*)\\)$`, 'u').exec(a))) return mk('f', [], m[1]);
  if ((m = new RegExp(`^∂²${fn}/∂${V}∂${V}${PT}$`, 'u').exec(a)))
    return mk('d2', [m[1]!, m[2]!], m[3]);
  if ((m = new RegExp(`^∂²${fn}/∂${V}²${PT}$`, 'u').exec(a))) return mk('d2', [m[1]!, m[1]!], m[2]);
  if ((m = new RegExp(`^∂${fn}/∂${V}${PT}$`, 'u').exec(a))) return mk('d1', [m[1]!], m[2]);
  if ((m = new RegExp(`^∇${fn}${PT}$`, 'u').exec(a))) return mk('grad', [], m[1]);
  if ((m = new RegExp(`^Δ${fn}${PT}$`, 'u').exec(a))) return mk('lap', [], m[1]);
  const c = spec.curve;
  if (c) {
    if ((m = new RegExp(`^${V}′\\(${esc(c.t)}\\)$`, 'u').exec(a))) return mk('cd', [m[1]!]);
    if ((m = new RegExp(`^${esc(c.g)}(″|′)?\\(([^()]*)\\)$`, 'u').exec(a))) {
      const kind = m[1] === '″' ? 'g2' : m[1] === '′' ? 'g1' : 'g';
      return m[2] === c.t ? mk(kind, []) : mk(kind, [], m[2]);
    }
  }
  return null;
}

/** la même dérivée sans le point */
function unpointed(ks: KPartState, h: Head): Head {
  const kind = h.kind;
  const key = h.key.replace(/@.*$/, '');
  const text =
    kind === 'f'
      ? ks.fn
      : kind === 'g' || kind === 'g1' || kind === 'g2'
        ? `${ks.curve!.g}${kind === 'g1' ? '′' : kind === 'g2' ? '″' : ''}(${ks.curve!.t})`
        : h.text.replace(/\([^()]*\)$/, '');
  return { key, text, kind, vars: h.vars };
}

/** la valeur exacte d'une dérivée (un couple pour le gradient) */
function headTerm(ks: KPartState, h: Head): Term | [Term, Term] | null {
  if (ks.termCache.has(h.key)) return ks.termCache.get(h.key)!;
  let out: Term | [Term, Term] | null = null;
  try {
    const [v1, v2] = ks.vars;
    const G = (): Term =>
      canon(substitute(ks.F, { [v1]: ks.curve!.X, [v2]: ks.curve!.Y } as Record<string, Term>));
    let base: Term | [Term, Term];
    switch (h.kind) {
      case 'f':
        base = ks.F;
        break;
      case 'd1':
        base = derive(ks.F, h.vars[0]!);
        break;
      case 'd2':
        base = derive(derive(ks.F, h.vars[1]!), h.vars[0]!);
        break;
      case 'grad':
        base = [derive(ks.F, v1), derive(ks.F, v2)];
        break;
      case 'lap':
        base = canon(add(derive(derive(ks.F, v1), v1), derive(derive(ks.F, v2), v2)));
        break;
      case 'cd':
        base = derive(h.vars[0] === v1 ? ks.curve!.X : ks.curve!.Y, ks.curve!.t);
        break;
      case 'g':
        base = G();
        break;
      case 'g1':
        base = derive(G(), ks.curve!.t);
        break;
      case 'g2':
        base = derive(derive(G(), ks.curve!.t), ks.curve!.t);
        break;
    }
    if (h.pt) {
      const inT = h.kind === 'g' || h.kind === 'g1' || h.kind === 'g2';
      const names = inT ? [ks.curve!.t] : ks.vars;
      if (h.pt.length !== names.length) return null;
      const sub = Object.fromEntries(names.map((n, i) => [n, exprTerm(h.pt![i]!)]));
      base = Array.isArray(base)
        ? [canon(substitute(base[0], sub)), canon(substitute(base[1], sub))]
        : canon(substitute(base, sub));
    }
    out = base;
  } catch {
    out = null;
  }
  ks.termCache.set(h.key, out);
  return out;
}

/* ------------------------------ lecture d'une ligne ------------------------------ */

interface Side {
  atoms: string[];
  heads: Head[];
  /** l'expression, dérivées en symboles (clé de la ligne) */
  S: Term | null;
  /** valeur : un terme, ou un couple (gradient) */
  T: Term | null;
  couple: [Term, Term] | null;
  /** le côté n'est qu'un bout « dérivée » */
  single: Head | null;
  size: number;
  hasVars: boolean;
  /** le côté dépend de t (le long d'une courbe) */
  hasT: boolean;
}

type Read =
  | { k: 'incomplete' }
  | { k: 'syntax'; title: string; message: string }
  | { k: 'eq'; sides: [Side, Side] };

function symOf(ks: KPartState, h: Head): string {
  let s = ks.syms.get(h.key);
  if (!s) {
    s = `Ψ${ks.syms.size}`;
    ks.syms.set(h.key, s);
  }
  return s;
}

function parseExpr(ks: KPartState, atoms: string[]): { S: Term; heads: Head[] } | null {
  const heads: Head[] = [];
  const mapped = atoms.map((a) => {
    const h = headOf(ks, a);
    if (!h) return a;
    heads.push(h);
    return symOf(ks, h);
  });
  let line: Line;
  try {
    line = parseLine(normalize(caretAtoms(mapped)), { chains: true });
  } catch {
    return null;
  }
  const d = line.dis[0];
  if (line.dis.length !== 1 || !d || d.cmp || d.rels || d.sides.length !== 1) return null;
  try {
    return { S: withE(fromNode(d.sides[0] as Node)), heads };
  } catch {
    return null;
  }
}

/** remplace les symboles des dérivées par leur valeur */
function valueOf(ks: KPartState, S: Term, heads: Head[]): Term | null {
  const sub: Record<string, Term> = {};
  for (const h of heads) {
    const v = headTerm(ks, h);
    if (!v || Array.isArray(v)) return null;
    sub[symOf(ks, h)] = v;
  }
  try {
    return canon(substitute(S, sub));
  } catch {
    return null;
  }
}

function readSide(ks: KPartState, atoms: string[]): Side | 'incomplete' | { err: string } {
  if (!atoms.length) return 'incomplete';
  let depth = 0;
  const top: number[] = [];
  for (let i = 0; i < atoms.length; i++) {
    const a = atoms[i]!;
    if (a === '(') depth++;
    if (a === ')') depth--;
    if (depth < 0) return 'incomplete';
    if (a === ',') {
      if (depth !== 1) return { err: 'couple' };
      top.push(i);
    }
  }
  if (depth !== 0) return 'incomplete';
  const vars = new Set<string>(ks.vars);
  if (top.length) {
    // un couple « (E₁, E₂) »
    if (top.length !== 1 || atoms[0] !== '(' || atoms[atoms.length - 1] !== ')')
      return { err: 'couple' };
    // la parenthèse ouverte au début se ferme à la fin
    let d = 0;
    for (let i = 0; i < atoms.length - 1; i++) {
      if (atoms[i] === '(') d++;
      if (atoms[i] === ')') d--;
      if (d === 0) return { err: 'couple' };
    }
    const p1 = atoms.slice(1, top[0]);
    const p2 = atoms.slice(top[0]! + 1, atoms.length - 1);
    if (!p1.length || !p2.length) return 'incomplete';
    const e1 = parseExpr(ks, p1);
    const e2 = parseExpr(ks, p2);
    if (!e1 || !e2) return 'incomplete';
    const heads = [...e1.heads, ...e2.heads];
    if (heads.some((h) => h.kind === 'grad')) return { err: 'grad' };
    const t1 = valueOf(ks, e1.S, e1.heads);
    const t2 = valueOf(ks, e2.S, e2.heads);
    if (!t1 || !t2) return { err: 'read' };
    const sy = new Set([...symbolsOf(e1.S), ...symbolsOf(e2.S)]);
    return {
      atoms,
      heads,
      S: null,
      T: null,
      couple: [t1, t2],
      single: null,
      size: size(e1.S) + size(e2.S),
      hasVars: [...sy].some((s) => vars.has(s)),
      hasT: !!ks.curve && sy.has(ks.curve.t),
    };
  }
  const e = parseExpr(ks, atoms);
  if (!e) return 'incomplete';
  const single = atoms.length === 1 ? (e.heads[0] ?? null) : null;
  if (e.heads.some((h) => h.kind === 'grad')) {
    if (!single) return { err: 'grad' };
    const v = headTerm(ks, single);
    if (!v || !Array.isArray(v)) return { err: 'read' };
    return {
      atoms,
      heads: e.heads,
      S: e.S,
      T: null,
      couple: v,
      single,
      size: 1,
      hasVars: false,
      hasT: false,
    };
  }
  const T = valueOf(ks, e.S, e.heads);
  if (!T) return { err: 'read' };
  return {
    atoms,
    heads: e.heads,
    S: e.S,
    T,
    couple: null,
    single,
    size: size(e.S),
    hasVars: [...symbolsOf(e.S)].some((s) => vars.has(s)),
    hasT: !!ks.curve && symbolsOf(e.S).has(ks.curve.t),
  };
}

function readAtoms(ks: KPartState, A: string[]): Read {
  const cut: number[] = [];
  let depth = 0;
  A.forEach((a, i) => {
    if (a === '(') depth++;
    if (a === ')') depth--;
    if (a === '=' && depth === 0) cut.push(i);
  });
  if (!cut.length) return { k: 'incomplete' };
  if (cut.length > 1)
    return {
      k: 'syntax',
      title: 'Une égalité',
      message: `Écris une égalité par ligne : « ∂${ks.fn}/∂${ks.vars[0]} = … ».`,
    };
  const l = readSide(ks, A.slice(0, cut[0]));
  const r = readSide(ks, A.slice(cut[0]! + 1));
  if (l === 'incomplete' || r === 'incomplete') return { k: 'incomplete' };
  for (const s of [l, r])
    if ('err' in s) {
      if (s.err === 'couple')
        return {
          k: 'syntax',
          title: 'Un couple',
          message: 'Écris un couple entre parenthèses : « (…, …) ».',
        };
      if (s.err === 'grad')
        return {
          k: 'syntax',
          title: 'Le gradient est un couple',
          message: `Écris « ∇${ks.fn} = (…, …) ».`,
        };
      return { k: 'syntax', title: 'Ça ne se lit pas', message: 'Vérifie l’écriture de ta ligne.' };
    }
  return { k: 'eq', sides: [l as Side, r as Side] };
}

/* ------------------------------ vérité ------------------------------ */

function envsFor(ks: KPartState, sides: Side[]): Record<string, number>[] {
  if (!ks.curve) return ks.grid;
  const t = ks.curve.t;
  const usesT = sides.some(
    (s) =>
      s.heads.some((h) => ['cd', 'g', 'g1', 'g2'].includes(h.kind)) ||
      (s.T ? symbolsOf(s.T).has(t) : false) ||
      (s.couple ? s.couple.some((c) => symbolsOf(c).has(t)) : false),
  );
  return usesT ? ks.tEnvs : ks.grid;
}

function valsOf(T: Term, envs: Record<string, number>[]): number[] {
  return envs.map((e) => val(T, e));
}

function sameVals(a: number[], b: number[]): boolean {
  let n = 0;
  for (let i = 0; i < a.length; i++) {
    const fa = Number.isFinite(a[i]!);
    const fb = Number.isFinite(b[i]!);
    if (fa !== fb) return false;
    if (!fa) continue;
    if (!near(a[i]!, b[i]!)) return false;
    n++;
  }
  return n >= 4;
}

/** les lettres permises : les variables, t, π */
function strangerOf(ks: KPartState, sides: Side[]): string | null {
  const ok = new Set<string>([...ks.vars, 'π', ...(ks.curve ? [ks.curve.t] : [])]);
  for (const s of sides)
    for (const t of s.couple ?? (s.T ? [s.T] : []))
      for (const n of symbolsOf(t)) if (!ok.has(n)) return n;
  return null;
}

type Truth = 'eq' | 'neq' | 'shape';

function truthOf(ks: KPartState, L: Side, R: Side): Truth {
  if (!!L.couple !== !!R.couple) return 'shape';
  const envs = envsFor(ks, [L, R]);
  if (L.couple && R.couple)
    return sameVals(valsOf(L.couple[0], envs), valsOf(R.couple[0], envs)) &&
      sameVals(valsOf(L.couple[1], envs), valsOf(R.couple[1], envs))
      ? 'eq'
      : 'neq';
  return sameVals(valsOf(L.T!, envs), valsOf(R.T!, envs)) ? 'eq' : 'neq';
}

/** « R vaut-il ce terme ? » (diagnostics) */
function sideIs(ks: KPartState, R: Side, T: Term | null): boolean {
  if (!T || !R.T) return false;
  const envs = envsFor(ks, [R]);
  return sameVals(valsOf(R.T, envs), valsOf(T, envs));
}

/* ------------------------------ l'état ------------------------------ */

export function createKPartState(model: ScratchModel): KPartState {
  const spec = model.kernel!;
  const ps = partialSpecOf(model);
  const ks: KPartState = {
    model,
    spec: ps,
    fn: ps.fn,
    vars: ps.vars,
    F: { k: 'num', v: { n: 0n, d: 1n } } as Term,
    Fsize: 0,
    curve: null,
    grid: [],
    tEnvs: [],
    target: { key: '', text: '' },
    answer: { size: 0, heads: 0, text: '' },
    facts: new Map(),
    written: new Set(),
    givenTexts: new Set(),
    path: spec.path ?? [],
    traps: [],
    heads: new Map(),
    syms: new Map(),
    termCache: new Map(),
    lines: 0,
    concluded: false,
  };
  // la fonction
  const startA = kpartLineAtoms(spec.start, ps);
  const eq = startA.indexOf('=');
  const rhs = parseExpr(ks, startA.slice(eq + 1));
  if (eq !== 1 || !rhs || rhs.heads.length)
    throw new PartialModelError(`Le départ est « f(x, y) = expression » : « ${spec.start} ».`);
  ks.F = canon(rhs.S);
  ks.Fsize = size(rhs.S);
  for (const s of symbolsOf(ks.F))
    if (!ks.vars.includes(s) && s !== 'π')
      throw new PartialModelError(`La fonction n’a que les variables ${ks.vars.join(', ')}.`);
  if (ps.curve) {
    if (ks.vars[0] !== 'x' || ks.vars[1] !== 'y')
      throw new PartialModelError('Le long d’une courbe, les variables sont x et y.');
    const t = ps.curve.t;
    if ([ks.fn, ...ks.vars, 'e', 'i'].includes(t) || ps.curve.g === ks.fn)
      throw new PartialModelError('La variable t et la fonction g ont d’autres lettres.');
    const X = exprTerm(ps.curve.x);
    const Y = exprTerm(ps.curve.y);
    for (const s of [...symbolsOf(X), ...symbolsOf(Y)])
      if (s !== t && s !== 'π') throw new PartialModelError('La courbe ne dépend que de t.');
    ks.curve = { t, g: ps.curve.g, X, Y };
    ks.tEnvs = T_PTS.map((s) => ({ [t]: s, x: val(X, { [t]: s }), y: val(Y, { [t]: s }) }));
  }
  ks.grid = GRID.map(([a, b]) => ({ [ks.vars[0]]: a, [ks.vars[1]]: b }));
  // la question et la réponse
  const tgtA = kpartLineAtoms(spec.goal.target, ps);
  const tgt = readSide(ks, tgtA);
  if (tgt === 'incomplete' || 'err' in tgt || !tgt.heads.length)
    throw new PartialModelError(`La question est une dérivée : « ${spec.goal.target} ».`);
  ks.target = { key: sideKey(ks, tgt), text: kpartText(tgtA) };
  const ansA = kpartLineAtoms(spec.answer, ps);
  const ar = readAtoms(ks, ansA);
  if (ar.k !== 'eq')
    throw new PartialModelError(`La réponse est une égalité : « ${spec.answer} ».`);
  const [L, R] = ar.sides;
  if (sideKey(ks, L) !== ks.target.key)
    throw new PartialModelError('La réponse commence par la question.');
  if (strangerOf(ks, [L, R])) throw new PartialModelError('Une lettre inconnue dans la réponse.');
  if (truthOf(ks, L, R) !== 'eq') throw new PartialModelError('La réponse est fausse.');
  ks.answer = { size: R.size, heads: R.heads.length, text: kpartText(ansA) };
  ks.givenTexts = new Set(partialGivenTexts(model).map((t) => kpartText(kpartLineAtoms(t, ps))));
  ks.traps = (model.traps ?? []).map((t) => ({
    text: kpartText(kpartLineAtoms(t.line, ps)),
    line: kpartLine(t.line, ps),
    title: t.title ?? 'Attention',
    why: t.why,
  }));
  return ks;
}

/** les lignes de l'énoncé : la fonction, puis la courbe (« x = cos(t) », « g(t) = f(x, y) ») */
export function partialGivenTexts(model: ScratchModel): string[] {
  const k = model.kernel!;
  const c = k.partial?.curve;
  if (!c) return [k.start];
  const ps = partialSpecOf(model);
  return [
    k.start,
    `${ps.vars[0]} = ${c.x}`,
    `${ps.vars[1]} = ${c.y}`,
    `${c.g}(${c.t}) = ${ps.fn}(${ps.vars.join(', ')})`,
  ];
}

/** la clé d'un membre gauche : la dérivée, ou l'expression de dérivées */
function sideKey(ks: KPartState, s: Side): string {
  if (s.single) return `h:${s.single.key}`;
  try {
    return `e:${termKey(canon(s.S!))}`;
  } catch {
    return `t:${kpartText(s.atoms)}`;
  }
}

/* ------------------------------ verdicts ------------------------------ */

interface Base {
  atoms: string[];
  line: Line;
}

function stepVerdict(
  base: Base,
  kind: StepKind,
  omove: PMove,
  final: boolean,
  label?: string,
  why?: string,
): KPartVerdict {
  const s = STEP[kind];
  return {
    kind: 'step',
    ...base,
    hora: s.hora,
    final,
    rule: {
      id: PARTIAL_STEP_IDS[kind],
      card: CARD,
      label: label ?? s.label,
      hora: s.hora,
      why: (why ?? s.why) + (final ? ' C’est la réponse.' : ''),
    },
    omove: { ...omove, ...(final ? { final } : {}) },
  };
}

function jumpVerdict(base: Base, omove: PMove, final: boolean, message?: string): KPartVerdict {
  return {
    kind: 'useless',
    ...base,
    jump: true,
    final,
    title: final ? 'Juste !' : 'Juste, mais tu sautes des étapes',
    message:
      message ??
      (final
        ? 'C’est la réponse. Détaille ton calcul, une étape par ligne, pour gagner des HORA. Tu peux garder cette ligne sans HORA.'
        : 'Ta ligne est juste. Détaille-la, une étape à la fois, pour gagner des HORA. Tu peux la garder sans HORA.'),
    omove: { ...omove, ...(final ? { final } : {}) },
  };
}

function useless(base: Base, title: string, message: string, omove?: PMove): KPartVerdict {
  return { kind: 'useless', ...base, title, message, ...(omove ? { omove } : {}) };
}

function falseVerdict(base: Base, title: string, message: string): KPartVerdict {
  return { kind: 'false', ...base, title, message };
}

const known = (ks: KPartState, key: string): boolean => ks.facts.has(`h:${key}`);

/* ------------------------------ jugement ------------------------------ */

function knownPrefix(ks: KPartState, A: string[]): boolean {
  return ks.path.some((t) => {
    let atoms: string[];
    try {
      atoms = kpartLineAtoms(t, ks.spec);
    } catch {
      return false;
    }
    return atoms.length > A.length && A.every((a, i) => atoms[i] === a);
  });
}

export function kpartJudge(
  ks: KPartState,
  chips: Chip[],
  opts?: { verify?: boolean },
): KPartVerdict {
  const A = normalize(chips.flatMap((c) => c.atoms));
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  const prefix = knownPrefix(ks, A);
  const incomplete = (): KPartVerdict =>
    opts?.verify
      ? { kind: 'syntax', atoms: A, title: 'Ligne inachevée', message: 'Termine ta ligne.' }
      : { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
  const r = readAtoms(ks, A);
  if (r.k === 'incomplete') return incomplete();
  if (r.k === 'syntax') {
    if (prefix && !opts?.verify) return { kind: 'building', atoms: A, complete: false };
    return { kind: 'syntax', atoms: A, line: lineOf(A), title: r.title, message: r.message };
  }
  const base: Base = { atoms: A, line: lineOf(A) };
  let v: KPartVerdict;
  try {
    v = judgeRead(ks, base, r.sides[0], r.sides[1]);
  } catch {
    v = {
      kind: 'syntax',
      ...base,
      title: 'Ça ne se lit pas',
      message: 'Cette ligne ne se lit pas : vérifie l’écriture.',
    };
  }
  if (prefix && !opts?.verify) {
    if (v.kind === 'step') return { ...v, kind: 'ready', complete: true };
    return { kind: 'building', atoms: A, complete: true };
  }
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return { ...v, complete: true };
}

function judgeRead(ks: KPartState, base: Base, L: Side, R: Side): KPartVerdict {
  const text = kpartText(base.atoms);
  if (ks.givenTexts.has(text))
    return useless(
      base,
      'C’est l’énoncé',
      'Cette ligne est dans l’énoncé : écris l’étape suivante.',
    );
  if (ks.written.has(text))
    return useless(base, 'Déjà écrit', 'Tu as déjà écrit cette ligne : écris l’étape suivante.');
  const trap = ks.traps.find((t) => t.text === text);
  if (trap)
    return {
      kind: 'trap',
      ...base,
      title: trap.title,
      message: trap.why,
      trap: { line: trap.line, title: trap.title, why: trap.why },
    };
  if (!L.heads.length)
    return {
      kind: 'syntax',
      ...base,
      title: 'Ce que tu calcules',
      message: `Commence ta ligne par ce que tu calcules : « ∂${ks.fn}/∂${ks.vars[0]} = … ».`,
    };
  const st = strangerOf(ks, [L, R]);
  if (st)
    return {
      kind: 'syntax',
      ...base,
      title: 'Une lettre inconnue',
      message: `Que vaut ${st} ? Les variables sont ${ks.vars.join(' et ')}${ks.curve ? `, et ${ks.curve.t}` : ''}.`,
    };
  const truth = truthOf(ks, L, R);
  if (truth !== 'eq') return diagnose(ks, base, L, R, truth);
  const key = sideKey(ks, L);
  const final =
    key === ks.target.key && R.size <= ks.answer.size && R.heads.length <= ks.answer.heads;
  const omove: PMove = {
    text,
    key,
    size: R.size,
    heads: R.heads.map((h) => h.key),
    vars: R.hasVars,
  };
  const prev = ks.facts.get(key);
  if (prev) return repeatVerdict(ks, base, R, prev, omove, final);
  return firstVerdict(ks, base, L, R, omove, final);
}

function firstVerdict(
  ks: KPartState,
  base: Base,
  L: Side,
  R: Side,
  omove: PMove,
  final: boolean,
): KPartVerdict {
  const h = L.single;
  const fn = ks.fn;
  const missing = (keys: string[], texts: string[]): string | null => {
    const i = keys.findIndex((k) => !known(ks, k));
    return i < 0 ? null : texts[i]!;
  };
  if (!h) {
    // une expression de dérivées (« x × ∂f/∂x + y × ∂f/∂y »)
    if (R.heads.length) return jumpVerdict(base, omove, final);
    const need = L.heads.filter((x) => x.kind !== 'f' && !known(ks, x.key));
    if (need.length)
      return jumpVerdict(base, omove, final, `Écris d’abord « ${need[0]!.text} = … ».`);
    return stepVerdict(
      base,
      'substitute',
      omove,
      final,
      'Remplacer',
      'On remplace chaque dérivée par son expression.',
    );
  }
  if (h.pt) {
    const b = unpointed(ks, h);
    if (R.heads.length) return jumpVerdict(base, omove, final);
    const label =
      h.kind === 'f'
        ? `Calculer ${fn}(${h.pt.join(', ')})`
        : h.kind === 'g' || h.kind === 'g1' || h.kind === 'g2'
          ? `Calculer en ${ks.curve!.t} = ${h.pt[0]}`
          : `Calculer en (${h.pt.join(', ')})`;
    if (b.kind === 'f' || known(ks, b.key)) return stepVerdict(base, 'point', omove, final, label);
    return jumpVerdict(base, omove, final, `Écris d’abord « ${b.text} = … ».`);
  }
  const [v1, v2] = ks.vars;
  const d1 = (v: string) => `∂${fn}/∂${v}`;
  switch (h.kind) {
    case 'f':
      if (R.heads.length || R.size >= ks.Fsize)
        return useless(
          base,
          'Rien de nouveau',
          'C’est la fonction de l’énoncé : écris l’étape suivante.',
        );
      return stepVerdict(base, 'simplify', omove, final, `Simplifier ${fn}`);
    case 'd1': {
      if (R.heads.length)
        return useless(base, 'Écris l’expression', `Écris ${h.text} avec ${v1} et ${v2}.`);
      const v = h.vars[0]!;
      const w = v === v1 ? v2 : v1;
      return stepVerdict(
        base,
        'first',
        omove,
        final,
        `Dériver par rapport à ${v}`,
        `On dérive par rapport à ${v} : ${w} est une constante.`,
      );
    }
    case 'd2': {
      if (R.heads.length)
        return useless(base, 'Écris l’expression', `Écris ${h.text} avec ${v1} et ${v2}.`);
      const [a, b] = h.vars as [string, string];
      if (known(ks, `d1:${b}`))
        return stepVerdict(base, 'second', omove, final, `Dériver ${d1(b)} par rapport à ${a}`);
      if (a !== b && known(ks, `d1:${a}`))
        return stepVerdict(
          base,
          'second',
          omove,
          final,
          `Dériver ${d1(a)} par rapport à ${b}`,
          'Une dérivée croisée : l’ordre des dérivations ne change rien (théorème de Schwarz).',
        );
      return jumpVerdict(
        base,
        omove,
        final,
        `Ta ligne est juste. Écris d’abord « ${d1(b)} = … », puis dérive-la : tu gagneras des HORA. Tu peux garder cette ligne sans HORA.`,
      );
    }
    case 'grad': {
      if (R.heads.length)
        return stepVerdict(
          base,
          'gradient',
          omove,
          final,
          'Gradient',
          'Le gradient est le couple des dérivées partielles.',
        );
      const m = missing([`d1:${v1}`, `d1:${v2}`], [d1(v1), d1(v2)]);
      if (m)
        return jumpVerdict(
          base,
          omove,
          final,
          `Ta ligne est juste. Écris d’abord « ${m} = … » : tu gagneras des HORA. Tu peux garder cette ligne sans HORA.`,
        );
      return stepVerdict(base, 'gradient', omove, final);
    }
    case 'lap': {
      if (R.heads.length) return stepVerdict(base, 'laplacian', omove, final);
      const m = missing(
        [`d2:${v1},${v1}`, `d2:${v2},${v2}`],
        [`∂²${fn}/∂${v1}²`, `∂²${fn}/∂${v2}²`],
      );
      if (m)
        return jumpVerdict(
          base,
          omove,
          final,
          `Ta ligne est juste. Écris d’abord « ${m} = … » : tu gagneras des HORA. Tu peux garder cette ligne sans HORA.`,
        );
      return stepVerdict(base, 'laplacian', omove, final);
    }
    case 'cd': {
      const v = h.vars[0]!;
      return stepVerdict(base, 'curve', omove, final, `Dériver ${v}(${ks.curve!.t})`);
    }
    case 'g':
      if (R.heads.length) return jumpVerdict(base, omove, final);
      return stepVerdict(base, 'compose', omove, final);
    case 'g1': {
      const c = ks.curve!;
      if (R.heads.some((x) => x.kind === 'd1')) return stepVerdict(base, 'chain', omove, final);
      if (known(ks, 'g'))
        return stepVerdict(base, 'derive', omove, final, `Dériver ${c.g}(${c.t})`);
      const m = missing(
        [`d1:${v1}`, `d1:${v2}`, `cd:${v1}`, `cd:${v2}`],
        [d1(v1), d1(v2), `${v1}′(${c.t})`, `${v2}′(${c.t})`],
      );
      if (!m) return stepVerdict(base, 'chain', omove, final);
      return jumpVerdict(
        base,
        omove,
        final,
        `Ta ligne est juste. Écris d’abord « ${m} = … » : tu gagneras des HORA. Tu peux garder cette ligne sans HORA.`,
      );
    }
    case 'g2': {
      const c = ks.curve!;
      if (known(ks, 'g1'))
        return stepVerdict(base, 'derive', omove, final, `Dériver ${c.g}′(${c.t})`);
      return jumpVerdict(
        base,
        omove,
        final,
        `Ta ligne est juste. Écris d’abord « ${c.g}′(${c.t}) = … » : tu gagneras des HORA. Tu peux garder cette ligne sans HORA.`,
      );
    }
  }
}

function repeatVerdict(
  ks: KPartState,
  base: Base,
  R: Side,
  prev: Fact,
  omove: PMove,
  final: boolean,
): KPartVerdict {
  const now = new Set(omove.heads);
  if (R.heads.length < prev.heads.length) {
    const gone = prev.heads.filter((k) => !now.has(k));
    const need = gone.filter((k) => !k.startsWith('f') && !known(ks, k));
    if (need.length) return jumpVerdict(base, omove, final);
    return stepVerdict(
      base,
      'substitute',
      omove,
      final,
      'Remplacer',
      'On remplace chaque dérivée par son expression.',
    );
  }
  if (ks.curve && prev.vars && !R.hasVars && R.hasT && R.heads.length <= prev.heads.length)
    return stepVerdict(
      base,
      'substitute',
      omove,
      final,
      `Remplacer ${ks.vars[0]} et ${ks.vars[1]}`,
      `On remplace ${ks.vars[0]} et ${ks.vars[1]} par leurs expressions en ${ks.curve.t}.`,
    );
  if (R.size < prev.size) {
    const recog = omove.heads.includes('f') && !prev.heads.includes('f');
    return stepVerdict(
      base,
      'compute',
      omove,
      final,
      recog ? `Reconnaître ${ks.fn}` : 'Calculer',
      recog ? `On reconnaît l’expression de ${ks.fn}.` : 'Un calcul.',
    );
  }
  if (final) return stepVerdict(base, 'compute', omove, final);
  return useless(
    base,
    'Juste, mais ça ne t’avance pas',
    'Ta ligne est juste, mais elle n’avance pas le calcul : simplifie, ou écris l’étape suivante.',
  );
}

/** une ligne fausse : ce qui ne va pas, jamais la bonne réponse */
function diagnose(ks: KPartState, base: Base, L: Side, R: Side, truth: Truth): KPartVerdict {
  const h = L.single;
  const fn = ks.fn;
  const [v1, v2] = ks.vars;
  if (truth === 'shape') {
    if (L.couple)
      return falseVerdict(
        base,
        'Le gradient est un couple',
        `Le gradient a deux coordonnées : « ∇${fn} = (∂${fn}/∂${v1}, ∂${fn}/∂${v2}) ».`,
      );
    return falseVerdict(
      base,
      'Pas un couple',
      'Ici, on attend un nombre ou une expression, pas un couple.',
    );
  }
  if (h && h.kind === 'grad' && L.couple && R.couple) {
    const envs = envsFor(ks, [L, R]);
    const a0 = valsOf(L.couple[0], envs);
    const a1 = valsOf(L.couple[1], envs);
    const b0 = valsOf(R.couple[0], envs);
    const b1 = valsOf(R.couple[1], envs);
    if (sameVals(a0, b1) && sameVals(a1, b0))
      return falseVerdict(
        base,
        'Dans l’ordre',
        `Le gradient s’écrit (∂${fn}/∂${v1}, ∂${fn}/∂${v2}) : la dérivée par rapport à ${v1} d’abord.`,
      );
    if (!sameVals(a0, b0))
      return falseVerdict(
        base,
        'La première coordonnée',
        `La première coordonnée est ∂${fn}/∂${v1} : elle ne va pas.`,
      );
    return falseVerdict(
      base,
      'La seconde coordonnée',
      `La seconde coordonnée est ∂${fn}/∂${v2} : elle ne va pas.`,
    );
  }
  if (h && !h.pt) {
    const term = (k: Head['kind'], vars: string[]): Term | null => {
      const t = headTerm(ks, { key: `${k}:${vars.join(',')}`, text: '', kind: k, vars });
      return t && !Array.isArray(t) ? t : null;
    };
    if (h.kind === 'd1') {
      const v = h.vars[0]!;
      const w = v === v1 ? v2 : v1;
      if (sideIs(ks, R, term('d1', [w])))
        return falseVerdict(
          base,
          'Pas la bonne variable',
          `Tu as dérivé par rapport à ${w}. Pour ∂${fn}/∂${v}, on dérive par rapport à ${v} : ${w} est une constante.`,
        );
      if (sideIs(ks, R, ks.F))
        return falseVerdict(
          base,
          'Tu n’as pas dérivé',
          `C’est ${fn} elle-même : dérive-la par rapport à ${v}.`,
        );
      return falseVerdict(
        base,
        'Pas la bonne dérivée',
        `Dérive par rapport à ${v} en gardant ${w} constant.`,
      );
    }
    if (h.kind === 'd2') {
      const [a, b] = h.vars as [string, string];
      if (sideIs(ks, R, term('d1', [b])) || sideIs(ks, R, term('d1', [a])))
        return falseVerdict(
          base,
          'Dérive deux fois',
          `C’est une dérivée première : dérive-la encore une fois${a === b ? ` par rapport à ${a}` : ''}.`,
        );
      if (a !== b && (sideIs(ks, R, term('d2', [a, a])) || sideIs(ks, R, term('d2', [b, b]))))
        return falseVerdict(
          base,
          'Une fois par rapport à chaque variable',
          `∂²${fn}/∂${a}∂${b} : on dérive une fois par rapport à ${b}, puis une fois par rapport à ${a}.`,
        );
      return falseVerdict(
        base,
        'Pas la bonne dérivée seconde',
        'Vérifie chacune des deux dérivations.',
      );
    }
    if (h.kind === 'lap')
      return falseVerdict(
        base,
        'Pas le laplacien',
        `Δ${fn} = ∂²${fn}/∂${v1}² + ∂²${fn}/∂${v2}² : vérifie les deux dérivées secondes.`,
      );
    if (h.kind === 'f')
      return falseVerdict(base, 'Ce n’est pas égal', `Ce n’est pas la fonction ${fn} de l’énoncé.`);
    if (h.kind === 'cd')
      return falseVerdict(
        base,
        'Pas la bonne dérivée',
        `Dérive ${h.vars[0]}(${ks.curve!.t}) par rapport à ${ks.curve!.t}.`,
      );
    if (h.kind === 'g')
      return falseVerdict(
        base,
        `Ce n’est pas ${ks.curve!.g}(${ks.curve!.t})`,
        `${ks.curve!.g}(${ks.curve!.t}) = ${fn}(${v1}, ${v2}) : remplace ${v1} et ${v2} par leurs expressions en ${ks.curve!.t}.`,
      );
    if (h.kind === 'g1' && R.heads.some((x) => x.kind === 'd1' || x.kind === 'cd'))
      return falseVerdict(
        base,
        'La règle de la chaîne',
        `${ks.curve!.g}′(${ks.curve!.t}) = ${v1}′(${ks.curve!.t}) × ∂${fn}/∂${v1} + ${v2}′(${ks.curve!.t}) × ∂${fn}/∂${v2}.`,
      );
    if (h.kind === 'g1' || h.kind === 'g2')
      return falseVerdict(base, 'Pas la bonne dérivée', 'Vérifie ta dérivation.');
  }
  if (h && h.pt)
    return falseVerdict(
      base,
      'Pas la bonne valeur',
      h.kind === 'g' || h.kind === 'g1' || h.kind === 'g2'
        ? `Remplace ${ks.curve!.t} par ${h.pt[0]}, puis calcule.`
        : `Remplace ${v1} et ${v2} par les coordonnées du point, puis calcule.`,
    );
  return falseVerdict(base, 'Ce n’est pas égal', 'Les deux membres de ta ligne ne sont pas égaux.');
}

/* ------------------------------ mise à jour ------------------------------ */

export function kpartCommit(ks: KPartState, v: KPartVerdict): void {
  const m = v.omove;
  if (!m) return;
  ks.written.add(m.text);
  ks.lines++;
  ks.facts.set(m.key, { size: m.size, heads: m.heads, vars: m.vars });
  if (m.final) ks.concluded = true;
}

export function kpartGoalReached(ks: KPartState): boolean {
  return ks.concluded;
}

function readText(ks: KPartState, text: string): Read | null {
  try {
    return readAtoms(ks, kpartLineAtoms(text, ks.spec));
  } catch {
    return null;
  }
}

/** la ligne est vraie */
export function kpartHolds(ks: KPartState, text: string): boolean {
  const r = readText(ks, text);
  if (!r || r.k !== 'eq') return false;
  const [L, R] = r.sides;
  if (!L.heads.length || strangerOf(ks, [L, R])) return false;
  return truthOf(ks, L, R) === 'eq';
}

/** la ligne est la réponse (la question, juste) */
export function kpartIsAnswer(ks: KPartState, text: string): boolean {
  const r = readText(ks, text);
  if (!r || r.k !== 'eq') return false;
  const [L, R] = r.sides;
  if (sideKey(ks, L) !== ks.target.key || strangerOf(ks, [L, R])) return false;
  return truthOf(ks, L, R) === 'eq';
}

/** une ligne déjà établie (le coup de pouce passe à la suivante) */
export function kpartEstablished(ks: KPartState, text: string): boolean {
  try {
    return ks.written.has(kpartText(kpartLineAtoms(text, ks.spec)));
  } catch {
    return false;
  }
}

const chipsOf = (atoms: string[]): Chip[] => atoms.map((a) => ({ atoms: [a], text: a }));

/** coup de pouce : la ligne du corrigé qui suit la dernière déjà écrite */
export function kpartNextMove(
  ks: KPartState,
): { line: Line; text: string; atoms: string[]; label: string } | null {
  if (ks.concluded) return null;
  let from = 0;
  ks.path.forEach((t, i) => {
    if (kpartEstablished(ks, t)) from = i + 1;
  });
  for (let i = from; i < ks.path.length; i++) {
    const t = ks.path[i]!;
    if (kpartEstablished(ks, t)) continue;
    const atoms = kpartLineAtoms(t, ks.spec);
    const v = kpartJudge(ks, chipsOf(atoms), { verify: true });
    return { line: lineOf(atoms), text: kpartText(atoms), atoms, label: v.rule?.label ?? '' };
  }
  return null;
}

export function kpartSolvePath(model: ScratchModel): {
  lines: { line: Line; text: string; atoms: string[]; v: KPartVerdict }[];
  reached: boolean;
} {
  const ks = createKPartState(model);
  const lines: { line: Line; text: string; atoms: string[]; v: KPartVerdict }[] = [];
  for (const t of ks.path) {
    const atoms = kpartLineAtoms(t, ks.spec);
    const v = kpartJudge(ks, chipsOf(atoms), { verify: true });
    if (v.kind !== 'step') break;
    kpartCommit(ks, v);
    lines.push({ line: lineOf(atoms), text: kpartText(atoms), atoms, v });
  }
  return { lines, reached: ks.concluded };
}

/* ------------------------------ palette ------------------------------ */

/** les opérations des dérivées partielles, dans l'ordre d'affichage */
export const PARTIAL_OPS = [
  '+',
  '−',
  '×',
  '÷',
  '(',
  ')',
  ',',
  '²',
  '^',
  '√',
  'exp',
  'ln',
  'cos',
  'sin',
  'π',
  '=',
];

/**
 * Les bouts (D6 : l'énoncé et le corrigé, jamais les pièges ni les choix) : les dérivées
 * écrites dans l'énoncé, le corrigé et la question, les variables (et t), la fonction si
 * le corrigé l'écrit seule, les nombres, les opérations utiles (« , » pour un couple).
 */
export function kpartPaletteParts(model: ScratchModel): {
  names: string[];
  numbers: string[];
  ops: string[];
} {
  const spec = model.kernel!;
  const ps = partialSpecOf(model);
  const texts = [...partialGivenTexts(model), ...(spec.path ?? []), spec.answer];
  const atoms = texts.flatMap((t) => {
    try {
      return kpartLineAtoms(t, ps);
    } catch {
      return [];
    }
  });
  const used = new Set(atoms);
  const heads = [
    ...new Set(
      [...kpartLineAtoms(spec.goal.target, ps), ...atoms].filter(
        (a) => isPartialHeadAtom(a) && a !== `${ps.fn}(${ps.vars.join(', ')})`,
      ),
    ),
  ];
  const names = [
    ...heads,
    ...ps.vars,
    ...(ps.curve ? [ps.curve.t] : []),
    ...(used.has(ps.fn) ? [ps.fn] : []),
    ...(used.has('e') ? ['e'] : []),
  ];
  const nums = [...new Set(atoms.filter((a) => isNum(a)))];
  const value = (s: string) => Number(s.replace(',', '.'));
  nums.sort((a, b) => value(a) - value(b) || a.localeCompare(b));
  const always = new Set(['+', '−', '×', '÷', '(', ')', '=']);
  const ops = PARTIAL_OPS.filter((o) => always.has(o) || used.has(o));
  const sups = [...new Set(atoms.filter((a) => /^[⁰¹³⁴⁵⁶⁷⁸⁹]+$/.test(a)))];
  if (sups.length)
    ops.splice(ops.indexOf('²') >= 0 ? ops.indexOf('²') + 1 : ops.indexOf('='), 0, ...sups);
  return { names, numbers: nums, ops };
}
